import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { errText, get } from '../api';
import {
  ACTION_LABEL,
  ActionBadge,
  Alert,
  Empty,
  fmtDate,
  humanize,
  PageHeader,
  SeverityBadge,
  Spinner,
  StatusBadge,
  useLoader,
} from '../components/ui';
import { ACTIONS, SEVERITIES, type MessageListItem, type Settings } from '../types';

const PAGE_SIZE = 50;

const TEXT_FILTERS = ['user_id', 'conversation_id', 'q'] as const;

/** Date input value (YYYY-MM-DD, local) to an ISO datetime with offset, as the API requires. */
function dateToIso(value: string, endOfDay: boolean) {
  if (!value) return undefined;
  const [y, m, d] = value.split('-').map(Number);
  const dt = endOfDay ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d, 0, 0, 0, 0);
  return Number.isNaN(dt.getTime()) ? undefined : dt.toISOString();
}

export function MessagesPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: settings } = useLoader(() => get<Settings>('/api/settings'), []);

  const actions = useMemo(() => (params.get('action') ?? '').split(',').filter(Boolean), [params]);
  const includeAllowed = params.get('include_allowed') === '1';

  const apiQuery = useMemo(
    () => ({
      action: params.get('action') || undefined,
      category: params.get('category') || undefined,
      severity: params.get('severity') || undefined,
      status: params.get('status') || undefined,
      review_status: params.get('review_status') || undefined,
      user_id: params.get('user_id') || undefined,
      conversation_id: params.get('conversation_id') || undefined,
      q: params.get('q') || undefined,
      from: dateToIso(params.get('from') ?? '', false),
      to: dateToIso(params.get('to') ?? '', true),
      moderated_only: includeAllowed ? 'false' : undefined,
      limit: PAGE_SIZE,
    }),
    [params, includeAllowed],
  );

  const [items, setItems] = useState<MessageListItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    const id = ++seq.current;
    setLoading(true);
    setError(null);
    get<{ messages: MessageListItem[]; next_cursor: string | null }>('/api/messages', apiQuery)
      .then((res) => {
        if (id !== seq.current) return;
        setItems(res.messages);
        setCursor(res.next_cursor);
      })
      .catch((err) => id === seq.current && setError(errText(err)))
      .finally(() => id === seq.current && setLoading(false));
  }, [apiQuery]);

  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const res = await get<{ messages: MessageListItem[]; next_cursor: string | null }>('/api/messages', { ...apiQuery, cursor });
      setItems((prev) => [...prev, ...res.messages]);
      setCursor(res.next_cursor);
    } catch (err) {
      setError(errText(err));
    } finally {
      setLoadingMore(false);
    }
  };

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };

  const toggleAction = (a: string) => {
    const set = new Set(actions);
    if (set.has(a)) set.delete(a);
    else set.add(a);
    update({ action: ACTIONS.filter((x) => set.has(x)).join(',') || null });
  };

  // Text inputs are applied on submit to avoid a request per keystroke.
  const [text, setText] = useState<Record<string, string>>({});
  useEffect(() => {
    setText(Object.fromEntries(TEXT_FILTERS.map((k) => [k, params.get(k) ?? ''])));
  }, [params]);
  const applyText = (e: FormEvent) => {
    e.preventDefault();
    update(Object.fromEntries(TEXT_FILTERS.map((k) => [k, text[k]?.trim() || null])));
  };

  const activeCount = [...params.keys()].length;
  const categories = settings ? Object.keys(settings.categories).filter((c) => c !== 'none') : [];

  return (
    <>
      <PageHeader title="Moderated messages" subtitle="Every decision MsgGuard made, newest first." />

      <section className="card filters">
        <div className="filter-row">
          <div className="filter-group">
            <span className="filter-label">Action</span>
            <div className="chips">
              {ACTIONS.map((a) => (
                <button
                  key={a}
                  type="button"
                  className={`chip chip-${a} ${actions.includes(a) ? 'on' : ''}`}
                  aria-pressed={actions.includes(a)}
                  onClick={() => toggleAction(a)}
                >
                  {ACTION_LABEL[a]}
                </button>
              ))}
            </div>
          </div>
          <label className="toggle-inline" title={actions.length ? 'Ignored when an action filter is selected' : undefined}>
            <input
              type="checkbox"
              checked={includeAllowed || actions.includes('allow')}
              disabled={actions.length > 0}
              onChange={(e) => update({ include_allowed: e.target.checked ? '1' : null })}
            />
            Include allowed
          </label>
        </div>
        <div className="filter-grid">
          <label className="field">
            <span>Category</span>
            <select value={params.get('category') ?? ''} onChange={(e) => update({ category: e.target.value || null })}>
              <option value="">Any</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {humanize(c)}
                </option>
              ))}
              {params.get('category') && !categories.includes(params.get('category')!) && (
                <option value={params.get('category')!}>{params.get('category')}</option>
              )}
            </select>
          </label>
          <label className="field">
            <span>Severity</span>
            <select value={params.get('severity') ?? ''} onChange={(e) => update({ severity: e.target.value || null })}>
              <option value="">Any</option>
              {SEVERITIES.filter((s) => s !== 'none').map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Review</span>
            <select value={params.get('review_status') ?? ''} onChange={(e) => update({ review_status: e.target.value || null })}>
              <option value="">Any</option>
              <option value="unreviewed">Unreviewed</option>
              <option value="confirmed">Confirmed</option>
              <option value="overridden">Changed by reviewer</option>
            </select>
          </label>
          <label className="field">
            <span>Processing</span>
            <select value={params.get('status') ?? ''} onChange={(e) => update({ status: e.target.value || null })}>
              <option value="">Any</option>
              <option value="queued">Queued</option>
              <option value="processing">Processing</option>
              <option value="done">Done</option>
              <option value="failed">Failed</option>
            </select>
          </label>
          <label className="field">
            <span>From</span>
            <input type="date" value={params.get('from') ?? ''} onChange={(e) => update({ from: e.target.value || null })} />
          </label>
          <label className="field">
            <span>To</span>
            <input type="date" value={params.get('to') ?? ''} onChange={(e) => update({ to: e.target.value || null })} />
          </label>
        </div>
        <form className="filter-grid filter-text" onSubmit={applyText}>
          <label className="field">
            <span>Search content</span>
            <input type="search" placeholder="Text contains…" value={text.q ?? ''} onChange={(e) => setText({ ...text, q: e.target.value })} />
          </label>
          <label className="field">
            <span>User ID</span>
            <input value={text.user_id ?? ''} onChange={(e) => setText({ ...text, user_id: e.target.value })} />
          </label>
          <label className="field">
            <span>Conversation ID</span>
            <input value={text.conversation_id ?? ''} onChange={(e) => setText({ ...text, conversation_id: e.target.value })} />
          </label>
          <div className="filter-buttons">
            <button className="btn btn-primary">Apply</button>
            <button type="button" className="btn" disabled={!activeCount} onClick={() => setParams(new URLSearchParams(), { replace: true })}>
              Reset
            </button>
          </div>
        </form>
      </section>

      <Alert>{error}</Alert>

      <section className="card card-flush">
        {loading ? (
          <Spinner />
        ) : items.length === 0 ? (
          <Empty>
            No messages match these filters.
            {!includeAllowed && !actions.length && ' Allowed messages are hidden; tick "Include allowed" to see them.'}
          </Empty>
        ) : (
          <div className="table-wrap">
            <table className="table table-hover table-messages">
              <thead>
                <tr>
                  <th>Received</th>
                  <th>Action</th>
                  <th>User</th>
                  <th className="col-content">Content</th>
                  <th>Category</th>
                  <th>Severity</th>
                  <th className="num">Risk</th>
                  <th>Review</th>
                </tr>
              </thead>
              <tbody>
                {items.map((m) => (
                  <tr key={m.id} onClick={() => navigate(`/messages/${m.id}`)} className="clickable">
                    <td className="nowrap muted m-received">{fmtDate(m.received_at)}</td>
                    <td className="m-action">
                      {m.status === 'done' ? <ActionBadge action={m.action} /> : <StatusBadge status={m.status} />}
                      {m.flags.includes('fragmented_content') && (
                        <span className="badge badge-frag" title="Part of fragmented content">
                          fragment
                        </span>
                      )}
                    </td>
                    <td className="nowrap m-user">
                      <span className="mono">{m.end_user_id}</span>
                      {m.end_user_name && <div className="muted small">{m.end_user_name}</div>}
                    </td>
                    <td className="col-content m-content">
                      <Link to={`/messages/${m.id}`} onClick={(e) => e.stopPropagation()} className="content-link">
                        {m.content}
                      </Link>
                      {m.conversation_id && <div className="muted small mono">#{m.conversation_id}</div>}
                    </td>
                    <td className="nowrap m-cat">{m.category && m.category !== 'none' ? humanize(m.category) : <span className="muted">—</span>}</td>
                    <td className="m-sev">
                      <SeverityBadge severity={m.severity} />
                    </td>
                    <td className="num mono m-risk">{m.risk == null ? '—' : m.risk.toFixed(2)}</td>
                    <td className="m-review">
                      {m.review_status ? <StatusBadge status={m.review_status} /> : <span className="muted small">unreviewed</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="table-foot">
            <span className="muted small">
              Showing {items.length} message{items.length === 1 ? '' : 's'}
            </span>
            {cursor && (
              <button className="btn" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
        )}
      </section>
    </>
  );
}
